import React, { useEffect, useState, useCallback } from 'react';
import { useDeviceClass } from './hooks/useOfflineDevice';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader, LogOut } from 'lucide-react';
import './App.css';
import Dashboard from './components/dashboard';
import { Authentication } from './components/auth';
import { AuthProvider, useAuth } from './context/authcontext';
import { PatientProvider } from './context/patientContext';
import { ToastProvider } from './components/common/Toast';
import { refreshSession, globalSignOut } from './services/auth/cognito.service';
import { TokenService } from './services/auth/token.service';
import MfaEnrollmentGate from './components/auth/mfa/MfaEnrollmentGate';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import BAASignPage from './components/auth/BAASignPage';
import OrganizationSetupPage from './components/auth/OrganizationSetupPage';
import LegalAcceptModal from './components/auth/LegalAcceptModal';
import AccountBanners, { hasAccountBanners, useAccountStateRefresh } from './components/subscription/AccountBanners';
import AccountSuspendedPage from './components/subscription/AccountSuspendedPage';
import { isAccountBlocked } from './utils/subscription';
import type { AuthUser } from './types/permissions';
import { canSignBaa, effectiveBaaStatus } from './utils/baaAccess';
import { isGuardianOnly } from './utils/portalAccess';
import { SUPPORT_EMAIL } from './utils/support';
import GuardianShell from './components/portal/GuardianShell';
import PortalAcceptPage from './components/portal/accept/PortalAcceptPage';
import peloraLogo from './assets/images/logofullblack.png';
import { cognitoConfig } from './config/app.config';
import OfflineGate, { OfflineGateMode } from './components/offline/OfflineGate';
import AppUpdater from './components/offline/AppUpdater';
import InstallGuidePage, { INSTALL_GUIDE_PATH } from './components/install/InstallGuidePage';
import { OfflineSession } from './services/offline/offlineSession';
import { ChartDrafts } from './services/offline/chartDrafts';
import { initOfflineRuntime, pendingOpCount } from './services/offline/offlineServices';
import FieldModeShell, { SyncCenterRoute, SYNC_CENTER_PATH } from './components/offline/FieldModeShell';
import VisitWorkspaceRoute from './components/offline/workspace/VisitWorkspaceRoute';
import { userFromIdToken } from './services/auth/sessionUser';
import PersonaBar from './components/demo/PersonaBar';
import SandboxBanner from './components/demo/SandboxBanner';
import { clearPersonaState, getActivePersona } from './utils/sandbox';
import NotificationRedirect from './components/notifications/NotificationRedirect';

// Phase 16C: repositories, outbox and the foreground sync engine follow the offline session
initOfflineRuntime();

// Tabs share sign-out through this channel (each tab has its own session)
const SESSION_CHANNEL = 'pelora-session';

/**
 * Restores the session on app mount and keeps AuthContext in sync with TokenService.
 * Access/ID tokens live in memory only, so after a reload the refresh token
 * (sessionStorage) is exchanged for new tokens before the user is restored.
 */
function SessionRestorer({ children, onRestored }: { children: React.ReactNode; onRestored: (authenticated: boolean) => void }) {
  const { setUser, setPermissionsLoaded } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      if (!TokenService.isAuthenticated()) {
        await refreshSession();
      }
      if (cancelled) return;
      const restored = userFromIdToken();
      if (restored) {
        setUser(restored);
        onRestored(true);
      } else {
        onRestored(false);
      }
    };
    restore();
    return () => { cancelled = true; };
  }, [setUser, onRestored]);

  // Tokens cleared anywhere (logout, failed refresh): drop the user and permissions
  useEffect(() => TokenService.onChange((authenticated) => {
    if (!authenticated) {
      setUser(null);
      setPermissionsLoaded(false);
    }
  }), [setUser, setPermissionsLoaded]);

  return <>{children}</>;
}

/** ACC-05: true when the backend says this user must enroll in MFA first */
function needsMfaEnrollment(user: AuthUser | null): boolean {
  return !!user?.mfa?.required && !user.mfa.enrolled;
}

/**
 * Load user permissions after authentication succeeds (from session restore)
 * 
 * This component:
 * 1. Waits for user to be authenticated (session restored on app mount by SessionRestorer)
 * 2. Calls GET /users/me/permissions to fetch user's permission set
 * 3. Stores permissions in AuthContext for all components to use
 * 4. Handles errors gracefully (app continues if permission fetch fails)
 * 5. Sets permissionsLoaded flag to indicate when permissions are ready
 * 
 * Note: After user signin from the login page, permissions are loaded
 * in AuthenticationComponent and this will be a no-op. This only runs
 * when restoring a session (refresh token) on app mount.
 */
function PermissionsInitializer({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, loadPermissions, error: permissionsError, setPermissionsLoaded } = useAuth();
  const userId = user?.id;
  const userType = user?.userType;
  const userPermissions = user?.permissions;

  useEffect(() => {
    // Only fetch permissions if user is authenticated and permissions are not present (undefined)
    // Do NOT re-fetch if permissions is an empty array (means user has no permissions)
    if (userId && typeof userPermissions === 'undefined') {
      console.log('📋 Loading user permissions...', { userId, userType });
      loadPermissions().finally(() => {
        // Mark permissions as loaded (whether successful or failed)
        console.log('📋 Permissions load completed, setting permissionsLoaded=true');
        setPermissionsLoaded(true);
      });
    } else if (userId && typeof userPermissions !== 'undefined') {
      // Permissions already loaded (e.g., from authentication flow)
      console.log('📋 Permissions already loaded, setting permissionsLoaded=true', { 
        userId, 
        userType,
        permissionCount: userPermissions?.length || 0 
      });
      setPermissionsLoaded(true);
    }
    // userType is only logged and is fixed for a signed-in user
  }, [userId, userType, userPermissions, loadPermissions, setPermissionsLoaded]);

  // Log warning if permissions failed to load (but don't block UI)
  if (permissionsError) {
    console.warn('⚠️ Failed to load permissions:', permissionsError);
    // App continues — backend will validate on API calls
  }

  // No loading UI - permissions load silently in the background
  return <>{children}</> as React.ReactElement;
}

type BaaDestination = 'dashboard' | 'sign' | 'waiting';

const BAA_PATHS: Record<BaaDestination, string> = {
  dashboard: '/dashboard',
  sign: '/baa/sign',
  waiting: '/organization/setup',
};

/**
 * Phase 5 BAA gating (ACC-03/04):
 * - accepted / grace: dashboard (grace shows a warning banner)
 * - none / expired: owners and admins must sign; with no BAA at all other staff wait;
 *   once a BAA has expired other staff keep read access (error banner).
 */
function baaDestination(user: AuthUser): BaaDestination {
  // Phase 12: guardians are not party to the agency BAA; the portal gate decides their access
  if (isGuardianOnly(user.permissions)) return 'dashboard';
  const status = effectiveBaaStatus(user);
  if (status === 'accepted' || status === 'grace') return 'dashboard';
  if (canSignBaa(user)) return 'sign';
  return status === 'expired' ? 'dashboard' : 'waiting';
}

/**
 * Shared gates for every signed-in route: permissions loaded, MFA enrollment (ACC-05),
 * then pending Terms of Service / Privacy Policy acceptance (ACC-02, blocking).
 */
function AccountGate({
  onLogout,
  children
}: {
  onLogout: () => void | Promise<void>;
  children: (user: AuthUser) => React.ReactElement;
}) {
  const { user, permissionsLoaded, refreshPermissions, accountSetupIncomplete } = useAuth();

  if (!permissionsLoaded || !user || typeof user.permissions === 'undefined') {
    return (
      <BaaLoadingPage
        onLogout={onLogout}
        failureMessage={accountSetupIncomplete ? ACCOUNT_SETUP_INCOMPLETE_MESSAGE : undefined}
      />
    );
  }
  if (needsMfaEnrollment(user)) {
    return <MfaEnrollmentGate onLogout={onLogout} />;
  }
  if (user.pendingLegal && user.pendingLegal.length > 0) {
    return (
      <main className="min-h-screen bg-slate-50">
        <LegalAcceptModal pendingLegal={user.pendingLegal} onAccepted={refreshPermissions} onLogout={onLogout} />
      </main>
    );
  }
  return children(user);
}

const ACCOUNT_SETUP_INCOMPLETE_MESSAGE =
  `Your organization account was not finished setting up. Please contact ${SUPPORT_EMAIL}.`;

function BaaLoadingPage({
  onLogout,
  failureMessage
}: {
  onLogout: () => void | Promise<void>;
  /** Shown at once instead of the spinner (a known, non-transient failure). */
  failureMessage?: string;
}) {
  const navigate = useNavigate();
  const [waitedTooLong, setTimedOut] = useState(false);
  const timedOut = waitedTooLong || !!failureMessage;
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setTimedOut(true), 10000);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const handleExit = async () => {
    setIsSigningOut(true);
    try {
      await onLogout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-white px-5 py-10 text-slate-800">
      <section className="flex w-full max-w-md flex-col items-center px-2 text-center">
        <img
          src={peloraLogo}
          alt="Pelora Health"
          className="mb-10 h-auto w-56 max-w-[80vw] object-contain"
        />
        {timedOut ? (
          <div className="flex flex-col items-center">
            <p className="max-w-sm text-base leading-6 text-slate-600" role="alert">
              {failureMessage || 'Something went wrong. Please try again after some time.'}
            </p>
            <button
              type="button"
              onClick={handleExit}
              disabled={isSigningOut}
              className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded bg-slate-900 px-5 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              <LogOut size={16} aria-hidden="true" />
              {isSigningOut ? 'Signing out...' : 'Exit'}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
            <Loader size={32} className="animate-spin text-teal-700" aria-hidden="true" />
            <p className="text-sm font-medium text-slate-600">Loading</p>
          </div>
        )}
      </section>
    </main>
  );
}

function BaaSignRoute({ onLogout }: { onLogout: () => void | Promise<void> }) {
  return (
    <AccountGate onLogout={onLogout}>
      {(user) => {
        if (!canSignBaa(user)) {
          return <Navigate to={BAA_PATHS[baaDestination(user)]} replace />;
        }
        // Owners/admins may also open the page during the grace period ("Review and sign")
        if (effectiveBaaStatus(user) === 'accepted') {
          return <Navigate to="/dashboard" replace />;
        }
        return <BAASignPage />;
      }}
    </AccountGate>
  );
}

function OrganizationSetupRoute({ onLogout }: { onLogout: () => void | Promise<void> }) {
  return (
    <AccountGate onLogout={onLogout}>
      {(user) => {
        const destination = baaDestination(user);
        if (destination !== 'waiting') {
          return <Navigate to={BAA_PATHS[destination]} replace />;
        }
        return <OrganizationSetupPage onLogout={onLogout} />;
      }}
    </AccountGate>
  );
}

/** Path of the export-only page for suspended and canceled accounts (Phase 13, Q9). */
export const ACCOUNT_SUSPENDED_PATH = '/account/suspended';

/**
 * Dashboard with the account banners (BAA grace / expired, then trial / past due / read-only /
 * cancel scheduled, Phase 13) above the main layout.
 */
function DashboardLayout({ user, onLogout }: { user: AuthUser; onLogout: () => void | Promise<void> }) {
  const navigate = useNavigate();
  useAccountStateRefresh();

  // Demo sandbox: the "viewing as" bar while a persona is signed in
  const personaActive = Boolean(getActivePersona());
  if (!hasAccountBanners(user) && !personaActive) {
    return <Dashboard onLogout={onLogout} />;
  }

  return (
    <div className="flex h-screen flex-col">
      <PersonaBar />
      {hasAccountBanners(user) && <AccountBanners onReviewBaa={() => navigate('/baa/sign')} />}
      {/* The dashboard sizes itself with h-screen; inside this column it fills the space left by the banner */}
      <div className="min-h-0 flex-1 [&_.h-screen]:h-full">
        <Dashboard onLogout={onLogout} />
      </div>
    </div>
  );
}

/** Phase 12 guardian shell; sign-out returns to the login page. */
function GuardianLayout({ onLogout }: { onLogout: () => void | Promise<void> }) {
  const navigate = useNavigate();
  const signOut = useCallback(async () => {
    await onLogout();
    navigate('/login', { replace: true });
  }, [navigate, onLogout]);
  // Demo sandbox: the persona bar (viewing as the seeded guardian) or the sample-data notice; both
  // render nothing for a normal tenant
  return (
    <>
      <PersonaBar />
      <SandboxBanner />
      <GuardianShell onSignOut={signOut} />
    </>
  );
}

/** Keeps the query string (e.g. ?billing=success from Stripe) when redirecting to the suspended page. */
function SuspendedRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: ACCOUNT_SUSPENDED_PATH, search: location.search }} replace />;
}

/**
 * Signed-in app: runs the account gates, then the subscription gate (suspended / canceled →
 * export-only page, Phase 13), then the BAA gate, then renders the dashboard.
 * Guardians (portal:read and no staff permission, Phase 12) get the guardian shell instead.
 */
export function AuthenticatedApp({ onLogout }: { onLogout: () => void | Promise<void> }) {
  return (
    <AccountGate onLogout={onLogout}>
      {(user) => {
        if (isGuardianOnly(user.permissions)) {
          return <GuardianLayout onLogout={onLogout} />;
        }
        if (isAccountBlocked(user.subscription)) {
          return <SuspendedRedirect />;
        }
        const destination = baaDestination(user);
        if (destination !== 'dashboard') {
          return <Navigate to={BAA_PATHS[destination]} replace />;
        }
        return <DashboardLayout user={user} onLogout={onLogout} />;
      }}
    </AccountGate>
  );
}

/**
 * Export-only page for suspended and canceled accounts (Phase 13, Q9). Anyone whose account is
 * not blocked (or a guardian) is sent back to the normal flow.
 */
export function AccountSuspendedRoute({ onLogout }: { onLogout: () => void | Promise<void> }) {
  return (
    <AccountGate onLogout={onLogout}>
      {(user) => {
        if (isGuardianOnly(user.permissions) || !isAccountBlocked(user.subscription)) {
          return <Navigate to="/dashboard" replace />;
        }
        return <AccountSuspendedPage onLogout={onLogout} />;
      }}
    </AccountGate>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  // Phase 16A: lock screen / offline read-only mode for clinicians who set up offline mode
  const [offlineMode, setOfflineMode] = useState<OfflineGateMode>('normal');

  // Single logout path (manual, idle timeout, other tab): revoke refresh tokens
  // with GlobalSignOut, clear in-memory tokens, tell other tabs, and replace
  // the history entry so Back does not return to a PHI screen.
  // Phase 16A: an explicit sign-out also erases this user's offline data from the device.
  // Phase 16C (design §6.3): with unsynced work on this device, warn first; "sign out and keep"
  // keeps the outbox and escrow encrypted and drops the patient packets.
  const [logoutWarning, setLogoutWarning] = useState<number | null>(null);
  const handleLogout = useCallback(async (options?: { keepQueue?: boolean; force?: boolean }) => {
    const keepQueue = options?.keepQueue === true;
    if (options?.force !== true && !keepQueue) {
      const pending = await pendingOpCount();
      if (pending > 0) {
        setLogoutWarning(pending);
        return;
      }
    }
    setLogoutWarning(null);
    clearPersonaState(); // demo sandbox: forget the owner's stashed session and the active persona
    await ChartDrafts.clearAll(); // Phase 18 M10: unsaved chart form text never outlives the session
    if (OfflineSession.getStore()) {
      if (keepQueue) await OfflineSession.signOutKeepingQueue().catch(() => undefined);
      else await OfflineSession.wipe().catch(() => undefined);
    }
    setOfflineMode('normal');
    await globalSignOut();
    try {
      const channel = new BroadcastChannel(SESSION_CHANNEL);
      channel.postMessage('logout');
      channel.close();
    } catch {
      // BroadcastChannel unsupported: other tabs time out on their own
    }
    window.history.replaceState(null, '', '/login');
    setIsAuthenticated(false);
  }, []);

  // Phase 16A: on a device with offline mode set up, a relaunch or reload opens the lock screen
  // (the PIN restores the session from the encrypted refresh token, online or not)
  const handleSessionRestored = useCallback(async (authenticated: boolean) => {
    if (OfflineSession.listIdentities().length > 0) {
      const restored = authenticated ? userFromIdToken() : null;
      const enrolled = restored ? await OfflineSession.isEnrolled(restored.tenantId, restored.id).catch(() => false) : false;
      if (!authenticated || enrolled) {
        if (authenticated) TokenService.clearTokens();
        setOfflineMode('locked');
        setIsAuthenticated(false);
        setSessionChecked(true);
        return;
      }
    }
    setIsAuthenticated(authenticated);
    setSessionChecked(true);
  }, []);

  // Phase 16A (design §6.3): lock instead of sign-out. Keys and tokens leave memory; the refresh
  // token stays only in the encrypted store, so the PIN can bring the session back.
  const lockApp = useCallback(() => {
    OfflineSession.lock();
    TokenService.clearTokens();
    try {
      const channel = new BroadcastChannel(SESSION_CHANNEL);
      channel.postMessage('lock');
      channel.close();
    } catch {
      // BroadcastChannel unsupported: other tabs lock on their own timers
    }
    setIsAuthenticated(false);
    setOfflineMode('locked');
  }, []);

  // Idle timeout: clinicians with unlocked offline data lock; everyone else is signed out
  const handleIdleTimeout = useCallback(() => {
    if (OfflineSession.status === 'unlocked') lockApp();
    else handleLogout();
  }, [lockApp, handleLogout]);

  const handleUsePassword = useCallback(() => {
    OfflineSession.lock();
    TokenService.clearTokens();
    setOfflineMode('normal');
    setIsAuthenticated(false);
    window.history.replaceState(null, '', '/login');
  }, []);

  // Handle successful sign in
  const handleSignIn = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  // Session timeout hook — uses constants from session.ts (respects DEV_MODE)
  const {
    showWarning,
    timeRemaining,
    extendSession
  } = useSessionTimeout({
    isAuthenticated: isAuthenticated || offlineMode === 'offline',
    onTimeout: handleIdleTimeout
  });
  const idleLocks = OfflineSession.status === 'unlocked';
  const idleDeviceClass = useDeviceClass();

  // Tokens cleared elsewhere (e.g. refresh failed in apiClient): show sign-in
  useEffect(() => TokenService.onChange((authenticated) => {
    if (!authenticated) setIsAuthenticated(false);
  }), []);

  // Logout in another tab logs this tab out too
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(SESSION_CHANNEL);
      channel.onmessage = (event) => {
        if (event.data === 'logout') {
          void ChartDrafts.clearAll();
          TokenService.clearTokens();
          setIsAuthenticated(false);
        } else if (event.data === 'lock' && OfflineSession.listIdentities().length > 0) {
          OfflineSession.lock();
          TokenService.clearTokens();
          setIsAuthenticated(false);
          setOfflineMode('locked');
        }
      };
    } catch {
      channel = null;
    }
    return () => channel?.close();
  }, []);

  // Back-button guard: a page restored from the back/forward cache is reloaded,
  // so cached PHI screens are never shown after logout
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return (
    <AuthProvider>
      <SessionRestorer onRestored={handleSessionRestored}>
      <PermissionsInitializer>
        <PatientProvider>
          <ToastProvider>
          <div className="App">
            {/* New builds apply silently at a safe moment; users never see or choose the version */}
            <AppUpdater signedIn={isAuthenticated || offlineMode === 'offline'} />
            {/* Session timeout warning dialog (Phase 18U U9: say what is lost; 48 px actions) */}
            {showWarning && (isAuthenticated || offlineMode === 'offline') && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 px-4">
              <div role="alertdialog" aria-modal="true" aria-labelledby="idle-warning-title" aria-describedby="idle-warning-text" className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                <h2 id="idle-warning-title" className="mb-3 text-xl font-bold">Are you still there?</h2>
                <p id="idle-warning-text" className="mb-3 text-base text-gray-800">
                  {idleLocks ? 'pelora will lock' : 'You will be signed out'} in {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')} because nothing has happened for a while.
                </p>
                {idleLocks ? (
                  <p className="mb-4 text-sm text-gray-700">Chart text you have not saved stays encrypted on this device.</p>
                ) : (
                  <p className="mb-4 text-sm text-amber-900" data-testid="idle-unsaved-warning">
                    Chart text you have not saved will be lost when you are signed out.
                    {idleDeviceClass === 'MOBILE' ? ' Turn on offline mode from My Day to keep drafts on this phone or tablet.' : ''}
                  </p>
                )}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={idleLocks ? lockApp : () => { handleLogout(); }}
                    className="min-h-[48px] rounded-lg bg-gray-200 px-4 font-medium hover:bg-gray-300"
                  >
                    {idleLocks ? 'Lock now' : 'Sign out now'}
                  </button>
                  <button
                    type="button"
                    autoFocus
                    onClick={extendSession}
                    className="min-h-[48px] rounded-lg bg-slate-800 px-5 font-semibold text-white hover:bg-slate-700"
                  >
                    Stay signed in
                  </button>
                </div>
              </div>
            </div>
          )}
        
          {logoutWarning !== null && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" role="dialog" aria-modal="true" aria-labelledby="logout-warning-title">
              <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                <h2 id="logout-warning-title" className="mb-2 text-lg font-semibold text-gray-900">{logoutWarning} item{logoutWarning === 1 ? '' : 's'} not synced</h2>
                <p className="mb-4 text-sm text-gray-700">Stay signed in to sync, or sign out and keep them encrypted on this device. They are sent the next time you sign in here.</p>
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button type="button" onClick={() => handleLogout({ keepQueue: true })} className="min-h-[44px] rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-800">Sign out and keep</button>
                  <button type="button" autoFocus onClick={() => setLogoutWarning(null)} className="min-h-[44px] rounded-lg bg-slate-800 px-4 text-sm font-semibold text-white">Stay signed in</button>
                </div>
              </div>
            </div>
          )}

          {!sessionChecked ? (
            <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
              <Loader size={32} className="animate-spin text-teal-700" aria-hidden="true" />
            </div>
          ) : (
          <OfflineGate
            mode={offlineMode}
            onModeChange={setOfflineMode}
            onAuthenticated={handleSignIn}
            onUsePassword={handleUsePassword}
            onRevoked={handleLogout}
          >
          <Router>
            {isAuthenticated && <FieldModeShell />}
            <Routes>
              {isAuthenticated ? (
                <>
                  {/* Phase 16C: Offline Visit Workspace and Sync Center */}
                  <Route path="/visit/:visitId" element={<VisitWorkspaceRoute />} />
                  <Route path={SYNC_CENTER_PATH} element={<SyncCenterRoute />} />
                  <Route path="/baa/sign" element={<BaaSignRoute onLogout={handleLogout} />} />
                  <Route path="/organization/setup" element={<OrganizationSetupRoute onLogout={handleLogout} />} />
                  <Route path="/dashboard" element={<AuthenticatedApp onLogout={handleLogout}/>} />
                  <Route path={ACCOUNT_SUSPENDED_PATH} element={<AccountSuspendedRoute onLogout={handleLogout} />} />
                  <Route path={INSTALL_GUIDE_PATH} element={<InstallGuidePage backTo="/dashboard" backLabel="Back to pelora" />} />
                  {/* Phase 16F: notification deep link (the dashboard opens it) */}
                  <Route path="/notifications/:id" element={<NotificationRedirect to="/dashboard" />} />
                  <Route path="/notifications" element={<NotificationRedirect to="/dashboard" />} />
                  <Route path="*" element={<BaaAwareRedirect onLogout={handleLogout} />} />
                </>
              ) : (
                <>                <Route
                    path="/login"
                    element={
                      <Authentication
                        region={cognitoConfig.region}
                        userPoolId={cognitoConfig.userPoolId}
                        clientId={cognitoConfig.clientId}
                        onSignedIn={handleSignIn}
                      />
                    }
                  />
                  <Route path="/portal/accept" element={<PortalAcceptPage onSignedIn={handleSignIn} />} />
                  {/* Phase 16A: public, shareable install guide (no PHI) */}
                  <Route path={INSTALL_GUIDE_PATH} element={<InstallGuidePage backTo="/login" backLabel="Back to sign-in" />} />
                  {/* Phase 16F: the email's link opens only after sign-in; the dashboard picks it up */}
                  <Route path="/notifications/:id" element={<NotificationRedirect to="/login" />} />
                  <Route path="/notifications" element={<NotificationRedirect to="/login" />} />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </>
              )}
            </Routes>
          </Router>
          </OfflineGate>
          )}
          </div>
          </ToastProvider>
        </PatientProvider>
      </PermissionsInitializer>
      </SessionRestorer>
    </AuthProvider>
  );
}

function BaaAwareRedirect({ onLogout }: { onLogout: () => void | Promise<void> }) {
  return (
    <AccountGate onLogout={onLogout}>
      {(user) => (isAccountBlocked(user.subscription) && !isGuardianOnly(user.permissions)
        ? <Navigate to={ACCOUNT_SUSPENDED_PATH} replace />
        : <Navigate to={BAA_PATHS[baaDestination(user)]} replace />)}
    </AccountGate>
  );
}

export default App;
